import { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react'
import { curationService } from '@/services/curation';
import { RawCurationPost } from '@/types/curation';
import { useToast } from '@/hooks/use-toast'
import { buildCurationParams, CurationFilters } from '@/utils/curation'
import { InfiniteScrollContainer } from '@/components/InfiniteScrollContainer'

export interface CurationContextType {
  posts: RawCurationPost[]
  isLoading: boolean
  isFetchingMore: boolean
  hasMore: boolean
  offset: number
  limit: number
  filters: CurationFilters
  setFilters: (_filters: CurationFilters) => void
  removePost: (_postId: string) => void
  updatePost: (_postId: string, _changes: Partial<RawCurationPost>) => void
  refetch: () => Promise<void>
}

export const CurationContext = createContext<CurationContextType | undefined>(undefined)

interface CurationProviderProps {
  children: ReactNode
  limit?: number
  filters?: CurationFilters
}

export function CurationProvider({ 
  children, 
  limit = 30,
  filters: initialFilters
}: CurationProviderProps) {
  const [posts, setPosts] = useState<RawCurationPost[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [offset, setOffset] = useState(0)
  const [filters, setFilters] = useState<CurationFilters>(initialFilters || {})
  const { toast } = useToast()

  const fetchPosts = useCallback(async (fetchOffset = 0, reset = false) => {
    try {
      if (reset) {
        setIsLoading(true)
      } else if (fetchOffset > 0) {
        setIsFetchingMore(true)
      } else {
        setIsLoading(true)
      }
      
      const params = buildCurationParams({
        offset: fetchOffset,
        limit,
        ...filters,
      })

      const response = await curationService.getPosts(params)
      
      if (reset) {
        setPosts(response.data)
      } else {
        setPosts(prev => [...prev, ...response.data])
      }
      
      setHasMore(response.remaining > 0)
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Erro ao carregar posts',
        description: 'Não foi possível carregar os posts. Tente novamente.',
      })
      console.error('Error fetching posts:', error)
    } finally {
      if (reset || fetchOffset === 0) {
        setIsLoading(false)
      }
      if (fetchOffset > 0) {
        setIsFetchingMore(false)
      }
    }
  }, [limit, filters, toast])

  const refetch = async () => {
    setOffset(0)
    setHasMore(true)
    await fetchPosts(0, true)
  }

  const removePost = (postId: string) => {
    setPosts(prev => prev.filter(post => post.id !== postId))
  }

  const updatePost = (postId: string, changes: Partial<RawCurationPost>) => {
    setPosts(prev => prev.map(post => post.id === postId ? { ...post, ...changes } : post))
  }

  const handleLoadMore = useCallback(() => {
    const newOffset = offset + limit
    setOffset(newOffset)
    fetchPosts(newOffset)
  }, [offset, limit, fetchPosts])

  useEffect(() => {
    setOffset(0)
    setHasMore(true)
    fetchPosts(0, true)
  }, [filters, fetchPosts])

  useEffect(() => {
    if (initialFilters) {
      setFilters(initialFilters)
    }
  }, [initialFilters])

  return (
    <CurationContext.Provider 
      value={{ 
        posts, 
        isLoading, 
        isFetchingMore,
        hasMore, 
        offset, 
        limit, 
        filters, 
        setFilters, 
        removePost, 
        updatePost,
        refetch 
      }}
    >
      <InfiniteScrollContainer
        hasMore={hasMore}
        isLoading={isLoading}
        isFetchingMore={isFetchingMore}
        onLoadMore={handleLoadMore}
      >
        {children}
      </InfiniteScrollContainer>
    </CurationContext.Provider>
  )
}

export function useCuration() {
  const context = useContext(CurationContext)
  if (context === undefined) {
    throw new Error('useCuration must be used within a CurationProvider')
  }
  return context
}

